import {
  RequirementContextType,
  RequirementDetailsAlertProps,
  RequirementStatusViewProps,
  RequirementTableItemData
} from "../types";
import { selectorIsRequirementExpiringSoon } from "../selectors/selectorIsRequirementExpiringSoon";
import { selectorIsRequirementExpired } from "../selectors/selectorIsRequirementExpired";
import { toWSDateString, WSButton } from "@wingspanhq/fe-component-library";
import { useRenewRequirementAsPayerModal } from "../components/RenewRequirementModal/useRenewRequirementAsPayerModal";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces";

type Props = {
  requirement: RequirementTableItemData;
  contextType: RequirementContextType;
  currentStatus: RequirementStatusViewProps;
};

const RenewalContent: React.FC<{
  requirement: RequirementTableItemData;
  currentStatus: RequirementStatusViewProps;
}> = ({ requirement, currentStatus }) => {
  const renewModal = useRenewRequirementAsPayerModal();

  return (
    <>
      <p>
        Requirement requires renewal. Complete the requirement to prevent
        payment delays.
      </p>

      <WSButton.Link
        size="S"
        rightIcon="chevron-right"
        onAsyncClick={async () => {
          await renewModal.open({
            requirement,
            currentStatus,
            onSuccess(requirement) {}
          });
        }}
      >
        Renew now
      </WSButton.Link>
    </>
  );
};

export const getRequirementOverrideExpirationAlert = ({
  requirement,
  contextType,
  currentStatus
}: Props): RequirementDetailsAlertProps[] => {
  const alerts: RequirementDetailsAlertProps[] = [];

  if (contextType === RequirementContextType.Payee) {
    if (selectorIsRequirementExpired(requirement)) {
      alerts.push({
        title: "Awaiting contractor",
        children:
          "Requirement is expired and currently awaiting contractor renewal.",
        theme: "error",
        icon: "alert-circle"
      });
    } else if (selectorIsRequirementExpiringSoon(requirement)) {
      alerts.push({
        title: "Awaiting contractor",
        children: "Requirement is currently awaiting contractor renewal.",
        theme: "info",
        icon: "info-circle"
      });

      if (requirement.replacedRequirement) {
        alerts.push({
          title: "Requirement expiring soon",
          children:
            "This requirement is expiring soon. Switch between the existing requirement and renewal tabs below during the transition period.",
          theme: "neutral",
          icon: "calendar"
        });
      }
    }
  } else {
    if (selectorIsRequirementExpired(requirement)) {
      alerts.push({
        title: "Requirement is expired",
        theme: "error",
        icon: "info-circle"
      });
    } else if (
      selectorIsRequirementExpiringSoon(requirement) &&
      requirement.replacedRequirement
    ) {
      alerts.push({
        title: "Requirement expiring soon",
        children:
          "This requirement is expiring soon. Switch between the existing requirement and renewal tabs below during the transition period.",
        theme: "neutral",
        icon: "calendar"
      });
    }

    if (
      (selectorIsRequirementExpired(requirement) ||
        selectorIsRequirementExpiringSoon(requirement)) &&
      requirement.manualRenewalAllowed
    ) {
      alerts.push({
        title: "Action required",
        children:
          "Requirement requires renewal. Complete the requirement to prevent payment delays.",
        theme: "neutral",
        icon: "calendar"
      });
    }
  }

  if (
    requirement.status === RequirementStatus.Complete &&
    requirement.effectiveDate
  ) {
    alerts.push({
      title: "Requirement renewal is complete",
      children: `This requirement renewal is complete and will automatically take effective on ${toWSDateString(
        requirement.effectiveDate,
        "input"
      )}. No additional action required at the moment.`,
      theme: "success",
      icon: "check-circle"
    });
  }

  return alerts;
};
